import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { omit } from 'es-toolkit';
import stableStringify from 'json-stable-stringify';
import { debounce } from 'lodash';
import { useCallback, useContext } from 'react';

import { TimesyncContext } from '../providers/TimesyncProvider/TimesyncProvider';
import BackendService from '../services/backendService';
import { Game, GameEditOrCreateDTO } from '../services/backendService/types';
import { Template } from '../services/TemplateService/Template.type';
import useAutosaveTemplate from './useAutosaveTemplate';
import useCreateGameFromTemplate from './useCreateGameFromTemplate';
import { gameCacheKey } from './useGame';
import { newRelicNoticeError } from './useNewRelic';
import useRouterMatch from './useRouterMatch';

function useGameAutoSave() {
  const client = useQueryClient();
  const { tenantId } = useRouterMatch();
  const { autoSaveTemplate } = useAutosaveTemplate();
  const { now } = useContext(TimesyncContext);

  const {
    createGameFromTemplate: createGameFromTemplatePresent,
    isCreatingGameFromTemplate,
    isCreatingGameFromTemplateSuccess,
  } = useCreateGameFromTemplate();

  const mutateGame = useCallback(
    async (mutateBody: GameEditOrCreateDTO) => {
      const cacheKey = [gameCacheKey, mutateBody?.gameId];
      const previousGame = client.getQueryData<Game>(cacheKey);
      client.setQueryData<Game | undefined>(cacheKey, game => {
        if (!game) return game;
        return {
          ...game,
          slides: mutateBody.slides === undefined ? game.slides : mutateBody.slides,
          attachments:
            mutateBody.attachments === undefined
              ? game.attachments
              : mutateBody.attachments,
          title: mutateBody.title === undefined ? game.title : mutateBody.title,
          topicId: mutateBody.topicId === undefined ? game.topicId : mutateBody.topicId,
          lastPlayedAt:
            mutateBody.lastPlayedAt === undefined
              ? game.lastPlayedAt || null
              : mutateBody.lastPlayedAt,
          currentRound:
            mutateBody.currentRound === undefined
              ? game.currentRound
              : mutateBody.currentRound,
          roundStartsAt:
            mutateBody.roundStartsAt === undefined
              ? game.roundStartsAt || null
              : mutateBody.roundStartsAt,
          isPlaying:
            mutateBody.isPlaying === undefined
              ? game.isPlaying || false
              : mutateBody.isPlaying,
          requireRealNames:
            mutateBody.requireRealNames === undefined
              ? game.requireRealNames
              : mutateBody.requireRealNames,
          isContentFilterEnabled:
            mutateBody.isContentFilterEnabled === undefined
              ? game.isContentFilterEnabled ?? true
              : mutateBody.isContentFilterEnabled,
          isShowingLobby:
            mutateBody.isShowingLobby === undefined
              ? game.isShowingLobby
              : mutateBody.isShowingLobby,
          settings:
            mutateBody.settings === undefined ? game.settings : mutateBody.settings,
        };
      });

      // Return the snapshotted value
      return () => client.setQueryData(cacheKey, previousGame);
    },
    [client],
  );
  const { mutate, isPending: isLoading, isError, isSuccess, isIdle } = useMutation({
    mutationFn: (variables: {
      gameConfig: GameEditOrCreateDTO;
      onSuccessFunction?: (gameId: string) => void;
    }) => {
      return BackendService.instance.game.autoSave({
        ...variables.gameConfig,
        modifiedAt: now(),
      });
    },
    onSuccess: (data, variables) => {
      client.setQueryData([gameCacheKey, data.id, 'success'], data);
      if (variables.onSuccessFunction) {
        variables.onSuccessFunction(data.id);
      }
    },
    onMutate: async mutateBody => {
      const cacheKey = [gameCacheKey, mutateBody?.gameConfig?.gameId, 'success'];
      const previousSuccessGame = client.getQueryData<Game>(cacheKey);
      return () => client.setQueryData(cacheKey, previousSuccessGame);
    },
    retryDelay: 2000,
    retry: (count: number, error: AxiosError) => {
      // due to SSR fetching we dont want to retry on 403s and 404s
      if (error.response?.status && [403, 404].includes(error.response?.status)) {
        return false;
      }

      // Default retry is 3 times
      if (count > 3) {
        return false;
      }
      return true;
    },
    onError: (err, bodyWithError, rollback) => {
      newRelicNoticeError(err, 'Error while auto saving game');

      //@ts-ignore
      return rollback();
    },
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSave = useCallback(
    debounce(
      async (
        draft: GameEditOrCreateDTO,
        server: GameEditOrCreateDTO,
        onSuccessFunction?: (gameId: string) => void,
      ) => {
        if (
          stableStringify(omit(draft, ['modifiedAt'])) !==
          stableStringify(omit(server, ['modifiedAt']))
        ) {
          mutate({ gameConfig: draft, onSuccessFunction });
        }
      },
      500,
    ),
    [],
  );
  const autoSave = useCallback(
    async (
      gameEditDTO: GameEditOrCreateDTO,
      onSuccessFunction?: (gameId: string) => void,
      template?: Template,
    ) => {
      const gameId = gameEditDTO.gameId || undefined;
      const key = [gameCacheKey, gameId];
      const serverGameCache = client.getQueryData<Game | undefined>(key);

      if (serverGameCache) {
        // to prevent race condition
        if (
          gameEditDTO.modifiedAt &&
          serverGameCache.modifiedAt &&
          gameEditDTO.modifiedAt < serverGameCache.modifiedAt
        )
          return;
        const serverGameDTO: GameEditOrCreateDTO = {
          ...gameEditDTO,
          slides: serverGameCache.slides || [],
          attachments: serverGameCache.attachments,
          currentRound: serverGameCache.currentRound,
          roundStartsAt: serverGameCache.roundStartsAt,
          lastPlayedAt: serverGameCache.lastPlayedAt,
          isPlaying: serverGameCache.isPlaying,
          requireRealNames: serverGameCache.requireRealNames,
          isContentFilterEnabled: serverGameCache.isContentFilterEnabled,
          settings: serverGameCache.settings,
        };
        await mutateGame(gameEditDTO);
        debouncedAutoSave(gameEditDTO, serverGameDTO, onSuccessFunction);
        return;
      }
      if (gameId) {
        const serverGame = await BackendService.instance.game.retrieve(gameId);
        if (serverGame) {
          // to prevent race condition when creating
          if (
            gameEditDTO.modifiedAt &&
            serverGame.modifiedAt &&
            gameEditDTO.modifiedAt < serverGame.modifiedAt
          )
            return;
          client.setQueryData(key, serverGame);
          const serverGameDTO: GameEditOrCreateDTO = {
            ...gameEditDTO,
            slides: serverGame.slides || [],
            attachments: serverGame.attachments,
            title: serverGame.title,
            currentRound: serverGame.currentRound,
            roundStartsAt: serverGame.roundStartsAt,
            lastPlayedAt: serverGame.lastPlayedAt,
            isPlaying: serverGame.isPlaying,
            requireRealNames: serverGame.requireRealNames,
            isContentFilterEnabled: serverGame.isContentFilterEnabled,
            settings: serverGame.settings,
          };
          await mutateGame(gameEditDTO);
          debouncedAutoSave(gameEditDTO, serverGameDTO, onSuccessFunction);
          return;
        }
      }

      if (
        template &&
        !template.content.activeGame?.id &&
        !isCreatingGameFromTemplate &&
        !isCreatingGameFromTemplateSuccess
      ) {
        const slides = gameEditDTO.slides || [];
        const roundToStartFrom =
          !!slides && slides.length > 0
            ? gameEditDTO.currentRound
              ? gameEditDTO.currentRound
              : slides[0].id
            : null;
        createGameFromTemplatePresent({
          activeTenantId: tenantId,
          isFromDiscover: false,
          templateConfig: {
            slides,
            id: template.id,
            metadata: template.metadata,
            requireRealNames: gameEditDTO.requireRealNames,
            isContentFilterEnabled: gameEditDTO.isContentFilterEnabled,
            currentRound: roundToStartFrom,
            modifiedAt: new Date().getTime(),
            attachments: template.attachments,
            settings: template.settings,
          },
          onSuccessFunction: (gameId: string) => {
            autoSaveTemplate({
              templateConfig: {
                id: template.id,
                modifiedAt: gameEditDTO.modifiedAt || new Date().getTime(),
                activeGame: {
                  isOngoing: false,
                  id: gameId,
                },
              },
            });
          },
        });
      }
    },
    [
      autoSaveTemplate,
      client,
      createGameFromTemplatePresent,
      debouncedAutoSave,
      isCreatingGameFromTemplate,
      isCreatingGameFromTemplateSuccess,
      mutateGame,
      tenantId,
    ],
  );
  return {
    autoSave,
    isLoading,
    isError,
    isSuccess,
    isIdle,
  };
}

export default useGameAutoSave;
